import { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import Breadcrumb from '../../components/common/Breadcrumb';
import AdminConsumerRegistration from '../../components/admin/AdminConsumerRegistration';
import Toast from '../../components/common/Toast';

/**
 * AdminConsumersPage
 * Main admin page for managing consumer registrations
 * Features: List view, toolbar (search/filter/button), pagination, drawer forms
 */
const AdminConsumersPage = () => {
  // State
  const [consumers, setConsumers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Filters
  const [filters, setFilters] = useState({
    verified: 'all', // all, verified, unverified
    active: 'all', // all, active, inactive
  });

  // Sorting & Pagination
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // UI State
  const [showForm, setShowForm] = useState(false);
  const [selectedConsumer, setSelectedConsumer] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [consumerToDelete, setConsumerToDelete] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  /**
   * Fetch consumers from API
   */
  const fetchConsumers = async () => {
    try {
      setLoading(true);

      // Build query params
      const params = new URLSearchParams();
      params.append('limit', itemsPerPage);
      params.append('offset', (currentPage - 1) * itemsPerPage);

      if (searchQuery) {
        params.append('search', searchQuery);
      }

      if (filters.verified !== 'all') {
        params.append('verified_email', filters.verified === 'verified');
      }

      if (filters.active !== 'all') {
        params.append('is_active', filters.active === 'active');
      }

      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);

      const response = await fetch(
        `https://vmc-backend-vt2w.onrender.com/api/admin/consumers?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch consumers');
      }

      const data = await response.json();
      setConsumers(data.data || []);
    } catch (error) {
      console.error('Error fetching consumers:', error);
      showToast('Failed to load consumers', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount and when filters change
  useEffect(() => {
    fetchConsumers();
    setCurrentPage(1); // Reset to first page on filter change
  }, [searchQuery, filters, sortBy, sortOrder]);

  // Refetch when page changes
  useEffect(() => {
    fetchConsumers();
  }, [currentPage]);

  /**
   * Handle create consumer
   */
  const handleCreate = () => {
    setSelectedConsumer(null);
    setShowForm(true);
  };

  /**
   * Handle edit consumer
   */
  const handleEdit = (consumer) => {
    setSelectedConsumer(consumer);
    setShowForm(true);
  };

  /**
   * Handle delete consumer
   */
  const handleDelete = (consumer) => {
    setConsumerToDelete(consumer);
    setShowDeleteModal(true);
  };

  /**
   * Confirm delete
   */
  const confirmDelete = async () => {
    try {
      setIsSubmitting(true);

      const response = await fetch(
        `https://vmc-backend-vt2w.onrender.com/api/admin/consumers/${consumerToDelete.id}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete consumer');
      }

      showToast('Consumer deleted successfully', 'success');
      setShowDeleteModal(false);
      setConsumerToDelete(null);
      fetchConsumers();
    } catch (error) {
      console.error('Error deleting consumer:', error);
      showToast('Failed to delete consumer', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handle form submission
   */
  const handleFormSubmit = async (formData) => {
    try {
      setIsSubmitting(true);

      const endpoint = selectedConsumer
        ? `https://vmc-backend-vt2w.onrender.com/api/admin/consumers/${selectedConsumer.id}`
        : 'https://vmc-backend-vt2w.onrender.com/api/admin/consumers';

      const method = selectedConsumer ? 'PUT' : 'POST';

      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to save consumer');
      }

      showToast(
        selectedConsumer ? 'Consumer updated successfully' : 'Consumer created successfully',
        'success'
      );

      setShowForm(false);
      setSelectedConsumer(null);
      fetchConsumers();
      setCurrentPage(1);
    } catch (error) {
      console.error('Error saving consumer:', error);
      showToast(error.message || 'Failed to save consumer', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Format date safely
   */
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return '—';
    }
  };

  /**
   * Show toast notification
   */
  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  /**
   * Calculate stats
   */
  const stats = {
    total: consumers.length || 0,
    verified: consumers.filter((c) => c.verified_email).length || 0,
    active: consumers.filter((c) => c.is_active).length || 0,
  };

  const breadcrumbItems = [
    { label: 'Admin', href: '/admin/dashboard' },
    { label: 'Consumers' },
  ];

  // Skeleton loader
  const SkeletonRow = () => (
    <tr className="border-b border-slate-200 hover:bg-slate-50">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <td key={i} className="px-4 py-3">
          <div className="h-4 bg-slate-200 rounded animate-pulse" />
        </td>
      ))}
    </tr>
  );

  return (
    <AdminLayout>
      <div className="space-y-6 p-6">
        {/* ── Breadcrumb & Page Title ── */}
        <div>
          <Breadcrumb items={breadcrumbItems} />
          <h1 className="text-[24px] font-bold text-slate-900 mt-3">Manage Consumers</h1>
          <p className="text-[13px] text-slate-500 mt-1">
            Create and manage consumer accounts on your platform
          </p>
        </div>

        {/* ── Stats Bar ── */}
        <div className="grid grid-cols-3 gap-4">
          <div className="p-4 bg-white border border-slate-200 rounded-lg">
            <p className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">
              Total Consumers
            </p>
            <p className="text-[28px] font-bold text-slate-900 mt-2">{stats.total}</p>
          </div>

          <div className="p-4 bg-white border border-slate-200 rounded-lg">
            <p className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">
              Email Verified
            </p>
            <p className="text-[28px] font-bold text-green-600 mt-2">{stats.verified}</p>
          </div>

          <div className="p-4 bg-white border border-slate-200 rounded-lg">
            <p className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">
              Active
            </p>
            <p className="text-[28px] font-bold text-blue-600 mt-2">{stats.active}</p>
          </div>
        </div>

        {/* ── Toolbar ── */}
        <div className="flex flex-col gap-3">
          {/* Search & Filter Row */}
          <div className="flex gap-3 items-center flex-wrap">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by name, email, phone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-3 py-2 text-[13px] text-slate-800 placeholder-slate-400 border border-slate-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
                <svg
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            {/* New Consumer Button */}
            <button
              onClick={handleCreate}
              className="px-4 py-2 text-[12px] font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <span>+</span> New Consumer
            </button>
          </div>

          {/* Filters */}
          <div className="flex gap-2 flex-wrap">
            {/* Verified Status */}
            <select
              value={filters.verified}
              onChange={(e) =>
                setFilters({ ...filters, verified: e.target.value })
              }
              className="px-3 py-1.5 text-[12px] border border-slate-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            >
              <option value="all">Email Verified: All</option>
              <option value="verified">Email Verified: Yes</option>
              <option value="unverified">Email Verified: No</option>
            </select>

            {/* Active Status */}
            <select
              value={filters.active}
              onChange={(e) =>
                setFilters({ ...filters, active: e.target.value })
              }
              className="px-3 py-1.5 text-[12px] border border-slate-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            >
              <option value="all">Status: All</option>
              <option value="active">Status: Active</option>
              <option value="inactive">Status: Inactive</option>
            </select>

            {/* Sort By */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-1.5 text-[12px] border border-slate-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            >
              <option value="name">Sort: Name</option>
              <option value="email">Sort: Email</option>
              <option value="created_at">Sort: Created Date</option>
              <option value="updated_at">Sort: Updated Date</option>
            </select>

            {/* Sort Order */}
            <button
              onClick={() =>
                setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
              }
              className="px-3 py-1.5 text-[12px] border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              title={`Toggle sort order`}
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>
        </div>

        {/* ── Table ── */}
        <div className="overflow-hidden border border-slate-200 rounded-lg bg-white">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-[12px] font-semibold text-slate-700">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-[12px] font-semibold text-slate-700">
                  Email
                </th>
                <th className="px-4 py-3 text-left text-[12px] font-semibold text-slate-700">
                  Phone
                </th>
                <th className="px-4 py-3 text-left text-[12px] font-semibold text-slate-700">
                  Country
                </th>
                <th className="px-4 py-3 text-left text-[12px] font-semibold text-slate-700">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-[12px] font-semibold text-slate-700">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <>
                  <SkeletonRow />
                  <SkeletonRow />
                  <SkeletonRow />
                </>
              ) : consumers.length > 0 ? (
                consumers.map((consumer) => (
                  <tr
                    key={consumer.id}
                    className="border-b border-slate-200 hover:bg-slate-50 transition-colors"
                  >
                    <td className="px-4 py-3 text-[13px] font-medium text-slate-900">
                      {consumer.name || '—'}
                    </td>
                    <td className="px-4 py-3 text-[12px] text-slate-600">
                      {consumer.email || '—'}
                    </td>
                    <td className="px-4 py-3 text-[12px] text-slate-600">
                      {consumer.profile_consumer?.phone || '—'}
                    </td>
                    <td className="px-4 py-3 text-[12px] text-slate-600">
                      {consumer.profile_consumer?.country || '—'}
                    </td>
                    <td className="px-4 py-3 text-[12px] space-y-1">
                      <div className="flex gap-1 flex-wrap">
                        {consumer.verified_email && (
                          <span className="inline-flex items-center px-2 py-0.5 bg-green-100 text-green-700 text-[10px] font-semibold rounded-full">
                            ✓ Email Verified
                          </span>
                        )}
                        {consumer.is_active ? (
                          <span className="inline-flex items-center px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-semibold rounded-full">
                            ✓ Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 bg-slate-100 text-slate-600 text-[10px] font-semibold rounded-full">
                            Inactive
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right space-x-1.5">
                      <button
                        onClick={() => handleEdit(consumer)}
                        className="inline-flex px-2 py-1 text-[11px] font-medium text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(consumer)}
                        className="inline-flex px-2 py-1 text-[11px] font-medium text-red-600 hover:bg-red-50 rounded transition-colors"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center">
                    <p className="text-[13px] text-slate-500">
                      No consumers found. Create your first consumer.
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ── */}
        {consumers.length > 0 && (
          <div className="flex items-center justify-between">
            <p className="text-[12px] text-slate-600">
              Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
              {Math.min(currentPage * itemsPerPage, stats.total + itemsPerPage)}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1 || loading}
                className="px-3 py-1.5 text-[12px] border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage((p) => p + 1)}
                disabled={consumers.length < itemsPerPage || loading}
                className="px-3 py-1.5 text-[12px] border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Right Drawer Form ── */}
      <AdminConsumerRegistration
        isOpen={showForm}
        onClose={() => {
          setShowForm(false);
          setSelectedConsumer(null);
        }}
        onSubmit={handleFormSubmit}
        initialData={selectedConsumer}
        isLoading={isSubmitting}
        title={selectedConsumer ? 'Edit Consumer' : 'Register Consumer'}
      />

      {/* ── Delete Confirmation Modal ── */}
      {showDeleteModal && consumerToDelete && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-[400px] w-full space-y-4 p-6">
            <div>
              <h3 className="text-[15px] font-bold text-slate-900">Delete Consumer?</h3>
              <p className="text-[13px] text-slate-600 mt-1">
                Are you sure you want to delete{' '}
                <strong>{consumerToDelete.name}</strong>? This action cannot be undone.
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setConsumerToDelete(null);
                }}
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 text-[12px] font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 text-[12px] font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Toast Notifications ── */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </AdminLayout>
  );
};

export default AdminConsumersPage;
